import React, { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import RootModalWithAction from "../common/RootModalWithAction";

import ClinicalAlertCreate from "./clinical-details/components/clinical-alerts/ClinicalAlertCreate";
import ClinicalAlertTable from "./clinical-details/components/clinical-alerts/ClinicalAlertTable";
import { fetchClinicalAlerts } from "../../redux/modules/patient/actions";

const EMPTY_ARRAY = [];
const getAlertId = (alert) => alert?.id ?? alert?.Id ?? null;

const ClinicalAlertListPopup = ({
  title,
  open,
  handleClose,
  patientId,
}) => {
  const dispatch = useDispatch();

  const clinicalAlerts = useSelector((state) => {
    const alertsState = state.patients?.clinicalAlerts;
    if (!alertsState) return EMPTY_ARRAY;
    const alerts = alertsState.ClinicalAlerts || alertsState.clinicalAlerts;
    return Array.isArray(alerts) ? alerts : EMPTY_ARRAY;
  });

  const [showCreateSlider, setShowCreateSlider] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [create, setCreate] = useState(true);
  const [tableFilter, setTableFilter] = useState("all");
  const [alertOrder, setAlertOrder] = useState([]);

  useEffect(() => {
    if (open && patientId) {
      dispatch(fetchClinicalAlerts(patientId));
    }
  }, [open, patientId, dispatch]);

  // Keep a stable row order so visibility toggles/refetches do not move rows.
  useEffect(() => {
    if (!open) return;

    const currentIds = clinicalAlerts.map(getAlertId).filter((id) => id != null);
    setAlertOrder((prev) => {
      if (!prev.length) return currentIds;

      const currentSet = new Set(currentIds);
      const prevSet = new Set(prev);
      return [
        ...prev.filter((id) => currentSet.has(id)),
        ...currentIds.filter((id) => !prevSet.has(id)),
      ];
    });
  }, [clinicalAlerts, open]);

  const orderIndex = useMemo(
    () => new Map(alertOrder.map((id, i) => [id, i])),
    [alertOrder]
  );

  const popupVisibleAlerts = useMemo(
    () =>
      [...clinicalAlerts].sort(
        (a, b) =>
          (orderIndex.get(getAlertId(a)) ?? Number.MAX_SAFE_INTEGER) -
          (orderIndex.get(getAlertId(b)) ?? Number.MAX_SAFE_INTEGER)
      ),
    [clinicalAlerts, orderIndex]
  );

  const getFilteredAlertsCount = () => {
    if (tableFilter === "all") return popupVisibleAlerts.length;
    
    return popupVisibleAlerts.filter((alert) => {
      const raw = alert?.viewStatus?.value || alert?.viewStatus || alert?.ViewType || alert?.viewType || "";
      const normalized = String(raw).toLowerCase().includes("pharmacist") ? "pharmacist-view" : "always-visible";
      return tableFilter === normalized;
    }).length;
  };

  const filteredAlertsCount = getFilteredAlertsCount();
  // Enable scroll when there are many records (for performance) or a single/last record
  const shouldShowScrollbar = filteredAlertsCount > 6 || filteredAlertsCount === 1;

  return (
    <>
      <RootModalWithAction
        open={open}
        handleClose={handleClose}
        className={`m-2 sm:m-4 !w-[98vw] sm:!w-[95vw] md:!w-[90vw] lg:!w-[80vw] xl:!w-[70vw] !max-w-[1140px]`}
        title={title}
        titleClassName="text-3xl text-teal-300"
        subTitle=""
        subTitleClassName={` text-sm-body text-teal-600 `}
        headClassName="px-5 py-3"
        showAdd={true}
        addAction={() => {
          setSelectedAlert(null);
          setCreate(true);
          setShowCreateSlider(true);
        }}
        addText="Add Clinical Alert"
        hideBorder={true}
        headerLayout="compact"
        closeOnOutsideClick={false}
      >
        <div className="relative flex flex-col items-start gap-0 px-3 sm:px-5 w-full flex-shrink-0 overflow-hidden max-h-[85vh]">
          <div
            className={`w-full flex-1 overflow-x-auto pb-5 mb-5 ${shouldShowScrollbar ? "overflow-y-auto scrollbar-hide max-h-[378px]" : "overflow-y-visible"}`}
          >
            <ClinicalAlertTable
              clinicalAlerts={popupVisibleAlerts}
              tableFilter={tableFilter}
              setTableFilter={setTableFilter}
              setShowCreateSlider={setShowCreateSlider}
              setSelectedAlert={setSelectedAlert}
              selectedAlert={selectedAlert}
              setCreate={setCreate}
              showTitle={false}
              borderless={true}
              onDeleted={() => {
                if (patientId) dispatch(fetchClinicalAlerts(patientId));
              }}
              onUpdated={() => {
                if (patientId) dispatch(fetchClinicalAlerts(patientId));
              }}
            />
          </div>
        </div>
      </RootModalWithAction>

      <ClinicalAlertCreate
        open={showCreateSlider}
        onClose={() => {
          setShowCreateSlider(false);
          setSelectedAlert(null);
          setCreate(true);
        }}
        selectedAlert={selectedAlert}
        patientId={patientId}
        create={create}
        clinicalAlerts={clinicalAlerts}
        setClinicalAlerts={() => {}}
        onSave={() => {
          if (patientId) dispatch(fetchClinicalAlerts(patientId));
        }}
        onOpenExistingAlertEdit={(existingAlert) => {
          setSelectedAlert(existingAlert);
          setCreate(false);
          setShowCreateSlider(true);
        }}
      />
    </>
  );
};

export default ClinicalAlertListPopup;


